import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Tooltip, Typography, Zoom, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { gerarComprovanteMensalidade, IComprovanteMensalidadeDados } from './ComprovanteMensalidade';

export interface IDialogVisualizarComprovanteProps {
  open: boolean;
  dados: IComprovanteMensalidadeDados | null;
  onClose: () => void;
}

// Visualizador do comprovante de baixa da mensalidade, embutido na própria tela (iframe
// dentro do diálogo) em vez de abrir uma aba nova — mesmo padrão do
// DialogVisualizarComprovante de Contas a Receber.
export const DialogVisualizarComprovante: React.FC<IDialogVisualizarComprovanteProps> = ({ open, dados, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const bgSubtle = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)';

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !dados) {
      setPdfUrl(null);
      return;
    }
    let cancelado = false;
    let urlAnterior: string | null = null;
    gerarComprovanteMensalidade(dados).then((blob) => {
      if (cancelado) return;
      const url = URL.createObjectURL(blob);
      urlAnterior = url;
      setPdfUrl(url);
    });
    return () => {
      cancelado = true;
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
    };
  }, [open, dados]);

  const imprimir = () => {
    if (!pdfUrl) return;
    const win = window.open(pdfUrl, '_blank');
    setTimeout(() => { win?.print(); }, 500);
  };

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'comprovante-mensalidade.pdf';
    link.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          background: theme.palette.background.default,
          maxHeight: '92vh',
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5, py: 1.75, borderBottom: `0.5px solid ${borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '8px', bgcolor: 'primary.main',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              Comprovante da mensalidade
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Confira o comprovante antes de imprimir
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 1, sm: 2 }, flex: 1, minHeight: 0, display: 'flex', bgcolor: bgSubtle, overflow: 'hidden' }}>
        {pdfUrl ? (
          <Box
            sx={{
              flex: 1, minHeight: 0, borderRadius: '10px', overflow: 'hidden',
              border: `0.5px solid ${borderColor}`, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', bgcolor: '#fff',
            }}
          >
            <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none', display: 'block' }} title="Comprovante da mensalidade" />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 1, borderRadius: '10px', border: `0.5px solid ${borderColor}`, color: 'text.disabled',
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" color="text.disabled">Gerando o comprovante...</Typography>
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          px: 2, py: 1.25, borderTop: `0.5px solid ${borderColor}`, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', bgcolor: theme.palette.background.paper, flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Imprimir">
            <IconButton
              onClick={imprimir}
              size="small"
              sx={{ border: `0.5px solid ${borderColor}`, color: 'text.secondary', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Baixar PDF">
            <IconButton
              onClick={downloadPdf}
              size="small"
              sx={{ border: `0.5px solid ${borderColor}`, color: 'text.secondary', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ borderColor, color: 'text.secondary' }}>
          Fechar
        </Button>
      </Box>
    </Dialog>
  );
};
